import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { X, PaperPlaneRight, EnvelopeSimple, CheckCircle, Headset } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Set REACT_APP_SUPPORT_EMAIL in frontend/.env (e.g. support@book-r.ai) to use a
// branded support address. Falls back to the founder's inbox until then.
export const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'milo.bookrai@gmail.com';

/** Open the support form from anywhere: openSupportForm() or openSupportForm('Prefilled subject'). */
export const openSupportForm = (subject) =>
  window.dispatchEvent(new CustomEvent('bookr:support', { detail: { subject } }));

/** A mailto: link that works with zero backend email setup. */
export const supportMailto = (subject = 'Bookr.ai support', body = '') =>
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const field = 'w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';
const label = 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 block';

export default function SupportContactModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Any part of the app can open this (footer button, AI assistant hand-off).
  useEffect(() => {
    const onOpen = (e) => {
      setSent(null); setError('');
      setForm((f) => ({
        ...f,
        name: f.name || user?.name || '',
        email: f.email || user?.email || '',
        subject: e?.detail?.subject || f.subject || '',
      }));
      setOpen(true);
    };
    window.addEventListener('bookr:support', onOpen);
    return () => window.removeEventListener('bookr:support', onOpen);
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return setError('Please enter a valid email address.');
    if (form.message.trim().length < 10) return setError('Please describe the issue in a little more detail.');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/support/ticket`, {
        name: form.name, email: form.email, subject: form.subject || 'Support request',
        message: form.message, page: window.location.pathname,
      });
      setSent(r.data);
      setForm((f) => ({ ...f, subject: '', message: '' }));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send just now — please use the email link below.');
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Contact support" data-testid="support-modal">
      <div className="absolute inset-0 bg-foreground/45 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative bg-card border border-border/70 rounded-3xl shadow-float w-full max-w-lg max-h-[90vh] overflow-y-auto fade-up">
        <button onClick={() => setOpen(false)} aria-label="Close" data-testid="support-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors cursor-pointer">
          <X size={18} />
        </button>

        {sent ? (
          <div className="p-8 text-center" data-testid="support-sent">
            <span className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} weight="fill" />
            </span>
            <h3 className="text-xl font-display font-semibold text-foreground">Message received</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Your request is logged{sent.ticket_id ? <> as <span className="font-mono text-foreground">{sent.ticket_id}</span></> : null}. Milo will get back to you at <span className="font-medium text-foreground">{form.email}</span>.
            </p>
            {sent.email_configured === false && (
              <p className="text-[12px] text-muted-foreground mt-4 bg-secondary/60 rounded-xl p-3 leading-relaxed">
                Heads up: automatic email isn't switched on yet, so this is saved in your dashboard rather than delivered to an inbox. To make sure it's seen right away, you can also{' '}
                <a href={supportMailto(form.subject || 'Bookr.ai support', form.message)} className="text-primary font-semibold hover:underline">send it by email</a>.
              </p>
            )}
            <button onClick={() => setOpen(false)} className="mt-6 bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim hover:bg-primary/90 cursor-pointer">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-7">
            <span className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
              <Headset size={22} weight="fill" />
            </span>
            <h3 className="text-xl font-display font-semibold text-foreground">Contact support</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-5">
              A real person reads these. Tell us what's going on and Milo will reply by email.
            </p>

            <div className="space-y-3.5">
              <div className="grid sm:grid-cols-2 gap-3.5">
                <div>
                  <span className={label}>Your name</span>
                  <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jordan" className={field} data-testid="support-name" />
                </div>
                <div>
                  <span className={label}>Email *</span>
                  <input type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@email.com" className={field} data-testid="support-email" />
                </div>
              </div>
              <div>
                <span className={label}>Subject</span>
                <input value={form.subject} onChange={(e) => set('subject', e.target.value)} placeholder="Problem with a booking" className={field} data-testid="support-subject" />
              </div>
              <div>
                <span className={label}>How can we help? *</span>
                <textarea required rows={5} value={form.message} onChange={(e) => set('message', e.target.value)}
                  placeholder="Tell us what happened — include a booking or event name if it helps." className={field + ' resize-none'} data-testid="support-message" />
              </div>
            </div>

            {error && <p className="text-sm text-accent-coral mt-3" data-testid="support-error">{error}</p>}

            <button type="submit" disabled={busy} data-testid="support-submit"
              className="w-full mt-5 bg-primary text-primary-foreground rounded-xl h-12 text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer">
              <PaperPlaneRight size={16} weight="fill" /> {busy ? 'Sending…' : 'Send to support'}
            </button>

            <div className="mt-4 pt-4 border-t border-border/60 text-center">
              <p className="text-[12px] text-muted-foreground">
                Prefer your own email app?{' '}
                <a href={supportMailto(form.subject || 'Bookr.ai support', form.message)} className="text-primary font-semibold hover:underline inline-flex items-center gap-1" data-testid="support-mailto">
                  <EnvelopeSimple size={13} weight="fill" /> {SUPPORT_EMAIL}
                </a>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
